/**
 * "구매시 결제 필수" — 기본설정의 신청금 항목 네 개를 한 번에 맞춘다.
 *
 * 단품은 결제를 받고 바로 확정하는 상품이 대부분이라, 신청금을 "전액·예약일 기준·즉시"로
 * 두는 조합만 반복해서 쓴다. 기본설정에 들어가 네 칸을 따로 고르는 대신 체크 하나로 끝낸다.
 *
 *   켬  신청금구분 004001(전체금액) · 신청금 100 · 신청금기준일 203002(예약일) · 신청금시한 0
 *   끔  신청금구분 004999(없음)
 *
 * ⚠ 저장은 기본설정 모달과 같은 SP(TRP_T_투어_상세정보_01)를 쓴다. 이 SP 는
 *   @예약시받을정보 로 그 상품의 예약정보필드를 통째로 갈아끼우므로, 지금 켜져 있는
 *   필드를 함께 보내지 않으면 "예약시 받을 정보"가 전부 꺼진다. 그래서 항상 읽고 → 합쳐서
 *   → 저장한다. 상세정보도 같은 이유로 읽은 값을 그대로 실어 보낸다.
 */

import { getProductDetailExtra, saveProductDetailExtra } from './apiService';

/** 신청금구분 코드. 004002~004004 는 일부만 받는 방식이라 이 화면에서는 만들지 않는다. */
export const DEPOSIT_KIND_FULL = '004001'; // 전체금액
export const DEPOSIT_KIND_NONE = '004999'; // 없음

/**
 * 신청금기준일 코드: 203001 출발일 / 203002 예약일.
 * 시한과 짝을 이뤄 "기준일 N일 전/후까지 신청금 입금"으로 읽힌다(203001 전, 203002 후).
 *
 * 상품을 만들 때 기본값이 '출발일 + 0일'인데, 단품은 출발일이 2036-12-31(날짜가 없어서
 * 정해 둔 자리)이라 그대로 두면 "2036년까지 내면 된다"가 된다. 전액 선결제일 때는
 * 이 값이 쓰이지 않아 드러나지 않지만, 나중에 부분결제로 바꾸는 순간 그대로 조건이 된다.
 * 그래서 예약일 기준으로 바꿔 둔다.
 */
export const DEPOSIT_BASIS_BOOKING_DATE = '203002';

/**
 * 켰을 때 들어갈 값.
 *
 * 완납기준일·완납시한은 일부러 건드리지 않는다. 잔금이 있을 때만 쓰이는 값이라
 * 신청금구분이 004001(전체금액)이면 서버가 아예 읽지 않는다. 세 곳에서 확인했다.
 *   TS_S_공급사상품검색_투어_가격번호_여행대분류  잔금 = CASE WHEN 신청금구분 IN ('004999','004001') THEN ''
 *   API_S_렌트카상세                              잔금납부방식 = CASE WHEN 신청금구분 = '004001' THEN ''
 *   API_S_B2C_투어상세                            004001 이면 '상품가 전체금액을 신청금 입금' 한 문장
 * 안 쓰이는 값을 덮어쓰면 나중에 원인 찾기만 어려워진다.
 */
const PREPAY_ON = {
  신청금구분: DEPOSIT_KIND_FULL,
  신청금: '100',
  신청금기준일: DEPOSIT_BASIS_BOOKING_DATE,
  신청금시한: 0,
};

/** 껐을 때 들어갈 값. 구분만 '없음' 으로 두면 나머지 값은 쓰이지 않는다. */
const PREPAY_OFF = {
  신청금구분: DEPOSIT_KIND_NONE,
};

/** 지금 "구매시 결제 필수" 상태인가 */
export function isPrepayRequired(detail) {
  return String(detail?.신청금구분 ?? '') === DEPOSIT_KIND_FULL;
}

/** 일부만 받는 신청금(판매총액 %, 고정금액)이 걸려 있는가 — 덮어쓰기 전에 물어보기 위함 */
export function isPartialDeposit(detail) {
  const kind = String(detail?.신청금구분 ?? '');
  return kind === '004002' || kind === '004003' || kind === '004004';
}

/**
 * 상품의 신청금 설정을 켜거나 끈다.
 *
 * @param {number|string} productNo
 * @param {boolean} required
 * @returns {Promise<object>} 저장 후의 상세정보 (화면 상태를 여기에 맞추면 된다)
 */
export async function setPrepayRequired(productNo, required) {
  const no = Number(productNo);
  if (!Number.isFinite(no)) throw new Error('상품번호가 없습니다.');

  const current = await getProductDetailExtra(no);
  const baseDetail = current.detail || {};

  // 기본설정 모달이 저장 전에 맞춰 주는 타입들. 여기서도 같이 맞추지 않으면 저장할 때마다
  // 값이 숫자↔문자로 흔들린다.
  const detail = {
    ...baseDetail,
    현지지불코드: String(baseDetail.현지지불코드 ?? '0'),
    연령성인끝: baseDetail.연령성인끝 ?? 100,
    ...(required ? PREPAY_ON : PREPAY_OFF),
  };

  const visibleFields = (current.requestFields || []).filter((f) => !!f.표시여부);

  await saveProductDetailExtra({ productNo: no, detail, requestFields: visibleFields });

  return detail;
}

/** 화면 진입 시 체크 상태를 알기 위해 상세정보만 읽는다. */
export async function loadPrepayState(productNo) {
  const no = Number(productNo);
  if (!Number.isFinite(no)) return null;
  const current = await getProductDetailExtra(no);
  return current.detail || null;
}
